import { ReactComponent as DownloadSvg } from "@/src/assets/icons/icon-download-black.svg";
import { Button } from "@/src/components/atom/Button";
import Checkbox from "@/src/components/atom/Checkbox";
import Loader from "@/src/components/atom/Loader";
import Typo from "@/src/components/atom/Typo";
import SectionCard from "@/src/components/molecule/SectionCard";
import { useContainerIdsFullBatchDownloadMutation } from "@/src/store/apis/containerImage";
import {
  useGetImporterTaskDetailContainerListQuery,
  useGetImporterTaskDetailQuery,
} from "@/src/store/apis/tasks/taskDetail";
import { ContainerListViewDto } from "@/src/store/apis/tasks/taskDetail/interface";
import colorSet, { ColorType } from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import { Accordion as AccordionRoot } from "@radix-ui/react-accordion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import ContainerAccordionItem from "@/src/components/organism/ContainerAccordionItem";

const containerEmptyArray: ContainerListViewDto[] = [];

interface ContainerTaskResultCardProps {
  loadingId?: number;
  bookingNo: string;
}

function ContainerTaskResultCard({
  bookingNo,
  loadingId,
}: ContainerTaskResultCardProps) {
  const { t } = useTranslation();

  // API
  const [containerIdsFullBatchDownload, containerIdsFullBatchDownloadData] =
    useContainerIdsFullBatchDownloadMutation();

  const { extraRemark, photoTypeRequests } = useGetImporterTaskDetailQuery(
    {
      id: loadingId as number,
    },
    {
      skip: !loadingId,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError }) => {
        const isUnstable = isUndefined(currentData) || isError;

        return {
          extraRemark: !isUnstable ? currentData.extraMediaRemark : null,
          // Loading 생성 시점의 displayOrder를 유지하기 위해 photoTypeRequests 추가
          photoTypeRequests: !isUnstable
            ? currentData.photoTypeRequests
            : undefined,
        };
      },
    },
  );
  const { containerList } = useGetImporterTaskDetailContainerListQuery(
    {
      id: loadingId as number,
    },
    {
      skip: !loadingId,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError }) => {
        const isUnstable = isUndefined(currentData) || isError;

        return {
          containerList: !isUnstable
            ? (currentData.rows ?? containerEmptyArray)
            : containerEmptyArray,
        };
      },
    },
  );

  // State
  const [containerCheckList, setContainerCheckList] = useState<number[]>([]);

  const selectContainerList = containerList.filter(({ id }) =>
    containerCheckList.includes(id),
  );

  const handleCheckboxClick = (checkboxItem: number) => {
    if (checkboxItem >= 0) {
      setContainerCheckList((prev) => {
        if (prev.includes(checkboxItem)) {
          return prev.filter((item) => item !== checkboxItem);
        }

        return [...prev, checkboxItem];
      });
    }
  };

  const handleContainerImageDownloadClick = async () => {
    if (containerCheckList.length === 0) {
      return;
    }

    try {
      await containerIdsFullBatchDownload({
        containerIdList: containerCheckList,
      }).unwrap();
    } catch (e: any) {
      console.error("[ContainerTaskResultCard] Download error:", e);
    }
  };

  return (
    <SectionCard
      cardTitle={t("contract:importer.detail.label.containerLoadingResult")}
    >
      <TitleContainer>
        <AllCheckboxTitle>
          <Checkbox
            onChange={(e) => {
              if (e.target.checked) {
                setContainerCheckList(containerList.map(({ id }) => id));
              } else {
                setContainerCheckList([]);
              }
            }}
            checked={
              containerList.length === containerCheckList.length &&
              containerList.length > 0
            }
          />

          <SelectContainerCount>
            <Typo typoType="h6" color="gray5">
              {t("task:detail.cards.containerList")}
            </Typo>

            <div>
              <Typo typoType="b6m" color="blue4">
                {containerCheckList.length}
              </Typo>
              <Typo typoType="b6m" color="gray7">
                /{containerList.length}
              </Typo>
            </div>
          </SelectContainerCount>
        </AllCheckboxTitle>
        {/* Buttons */}
        <TitleButtonContainer>
          {/* Download */}
          <StyledButton
            buttonSize={32}
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={handleContainerImageDownloadClick}
            disabled={
              containerIdsFullBatchDownloadData.isLoading ||
              selectContainerList.length === 0
            }
          >
            {containerIdsFullBatchDownloadData.isLoading ? (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            ) : (
              <>
                <DownloadIcon
                  color={selectContainerList.length === 0 ? "gray8" : "gray2"}
                />
                {t("task:detail.cards.download")}
              </>
            )}
          </StyledButton>
        </TitleButtonContainer>
      </TitleContainer>
      <AccordionRoot type="multiple">
        {containerList.length >= 1 &&
          containerList.map((item, idx) => {
            return (
              <ContainerAccordionItem
                role="importer"
                containerNo={item.containerNo}
                sealNo={item.sealNo}
                containerId={item.id}
                onCheckChange={(containerId) =>
                  handleCheckboxClick(containerId)
                }
                extraRemark={extraRemark}
                key={item.id}
                containerCheckList={containerCheckList}
                index={idx}
                photoTypeRequests={photoTypeRequests}
              />
            );
          })}
      </AccordionRoot>
    </SectionCard>
  );
}

export default ContainerTaskResultCard;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 12px 16px;
`;

const TitleButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DownloadIcon = styled(DownloadSvg)<{ color: ColorType }>`
  path {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const LoaderContainer = styled.figure`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 89px;
  text-align: center;
`;

const AllCheckboxTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectContainerCount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
